// @figma file=pkCGby54zXzTsccXvHqzO6 node=3870:24176 name="Rating" set="mytaible-shadcn-studio" verified=2026-03-22

import { cva, type VariantProps } from 'class-variance-authority';
import { Star } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Variants ─────────────────────────────────────────────────────────────────

/**
 * Size variants for the star container gap.
 *
 * sm → gap-0.5   (compact, e.g. inline in a list)
 * md → gap-1     (default)
 * lg → gap-1.5   (prominent, e.g. product hero)
 */
export const ratingContainerVariants = cva('inline-flex items-center', {
  variants: {
    size: {
      sm: 'gap-0.5',
      md: 'gap-1',
      lg: 'gap-1.5',
    },
  },
  defaultVariants: { size: 'md' },
});

/**
 * Size variants for individual star icons.
 *
 * sm → 16 × 16 px  (w-4 h-4)
 * md → 20 × 20 px  (w-5 h-5) — default
 * lg → 24 × 24 px  (w-6 h-6)
 */
export const ratingStarVariants = cva(
  ['inline-flex items-center justify-center shrink-0', 'transition-colors duration-100'],
  {
    variants: {
      size: {
        sm: 'w-4 h-4',
        md: 'w-5 h-5',
        lg: 'w-6 h-6',
      },
    },
    defaultVariants: { size: 'md' },
  },
);

// ─── Types ────────────────────────────────────────────────────────────────────

export type RatingSize = 'sm' | 'md' | 'lg';

export interface RatingProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof ratingContainerVariants> {
  /** Current rating value (controlled). 0 = no rating selected. */
  value?: number;
  /** Initial rating value (uncontrolled). Default: `0`. */
  defaultValue?: number;
  /** Callback fired when the user selects a new rating. */
  onChange?: (value: number) => void;
  /** Maximum number of stars. Default: `5`. */
  max?: number;
  /**
   * When `true`, the widget is non-interactive.
   * Renders as `role="img"` with a summary `aria-label`.
   * Default: `false`.
   */
  readOnly?: boolean;
  /**
   * When `true`, the widget is visible but non-interactive and visually dimmed.
   * Default: `false`.
   */
  disabled?: boolean;
  /** Star icon size. Default: `'md'`. */
  size?: RatingSize;
  className?: string;
}

// ─── Rating ───────────────────────────────────────────────────────────────────

/**
 * Star rating component supporting interactive input mode and read-only display mode.
 *
 * **Interactive mode** (default):
 * - Uses `role="radiogroup"` with each star rendered as `role="radio"`.
 * - Only the currently selected star has `aria-checked="true"`.
 * - Stars 1 → `value` are visually filled; stars `value+1` → `max` are empty.
 * - Keyboard: `ArrowRight`/`ArrowUp` to increase rating;
 *   `ArrowLeft`/`ArrowDown` to decrease (minimum 1).
 * - Tab enters the group on the selected star (or star 1 when unrated).
 *
 * **Read-only mode** (`readOnly={true}`):
 * - Renders a `role="img"` container.
 * - `aria-label` summarises the rating (e.g. "Rating: 3 out of 5 stars").
 * - Stars are `aria-hidden` — the label conveys all information.
 *
 * @example Controlled interactive
 * ```tsx
 * const [rating, setRating] = React.useState(0);
 * <Rating value={rating} onChange={setRating} />
 * ```
 *
 * @example Read-only display
 * ```tsx
 * <Rating value={4} max={5} readOnly />
 * ```
 *
 * @example Sizes
 * ```tsx
 * <Rating defaultValue={4} size="sm" />
 * <Rating defaultValue={4} size="md" />
 * <Rating defaultValue={4} size="lg" />
 * ```
 */
const Rating = React.forwardRef<HTMLDivElement, RatingProps>(
  (
    {
      value: valueProp,
      defaultValue = 0,
      onChange,
      max = 5,
      readOnly = false,
      disabled = false,
      size = 'md',
      className,
      'aria-label': ariaLabelProp,
      ...props
    },
    ref,
  ) => {
    // ── Controlled / uncontrolled ───────────────────────────────────────────
    const isControlled = valueProp !== undefined;
    const [internalValue, setInternalValue] = React.useState<number>(defaultValue);
    const value = isControlled ? (valueProp as number) : internalValue;

    // ── Hover preview (interactive mode only) ──────────────────────────────
    const [hoverValue, setHoverValue] = React.useState<number | null>(null);

    // Visual fill level — hover preview overrides committed value
    const displayValue = hoverValue !== null ? hoverValue : value;

    // ── Refs for programmatic focus (roving tabIndex) ──────────────────────
    const starRefs = React.useRef<(HTMLButtonElement | null)[]>([]);

    // ── Commit helper ──────────────────────────────────────────────────────
    const commitValue = React.useCallback(
      (next: number) => {
        if (readOnly || disabled) return;
        if (!isControlled) setInternalValue(next);
        onChange?.(next);
      },
      [readOnly, disabled, isControlled, onChange],
    );

    // ── Keyboard navigation ────────────────────────────────────────────────
    const handleKeyDown = React.useCallback(
      (e: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        if (readOnly || disabled) return;
        // `index` is 0-based; star number is `index + 1`
        if (e.key === 'ArrowRight' || e.key === 'ArrowUp') {
          e.preventDefault();
          const next = Math.min(index + 2, max);
          commitValue(next);
          starRefs.current[next - 1]?.focus();
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') {
          e.preventDefault();
          const prev = Math.max(index, 1);
          commitValue(prev);
          starRefs.current[prev - 1]?.focus();
        }
      },
      [readOnly, disabled, max, commitValue],
    );

    // ── Read-only mode ─────────────────────────────────────────────────────
    if (readOnly) {
      return (
        <div
          ref={ref}
          role="img"
          aria-label={ariaLabelProp ?? `Rating: ${value} out of ${max} stars`}
          className={cn(ratingContainerVariants({ size }), className)}
          {...props}
        >
          {Array.from({ length: max }, (_, i) => {
            const isFilled = i < value;
            return (
              <span key={i} aria-hidden="true" className={cn(ratingStarVariants({ size }))}>
                <Star
                  className={cn(
                    'w-full h-full',
                    isFilled
                      ? 'fill-current stroke-primary text-primary'
                      : 'fill-none text-muted-foreground/40',
                  )}
                />
              </span>
            );
          })}
        </div>
      );
    }

    // ── Interactive mode ───────────────────────────────────────────────────
    // Roving tabIndex: the TAB entry point is the selected star (1-based → 0-based),
    // or star 1 when value === 0 (unrated).
    const tabEntryIndex = value > 0 ? value - 1 : 0;

    return (
      <div
        ref={ref}
        role="radiogroup"
        aria-label={ariaLabelProp ?? 'Rating'}
        aria-disabled={disabled || undefined}
        className={cn(
          ratingContainerVariants({ size }),
          disabled && 'opacity-50 cursor-not-allowed',
          className,
        )}
        {...props}
      >
        {Array.from({ length: max }, (_, i) => {
          const starNumber = i + 1;
          const isFilled = i < displayValue;
          const isChecked = starNumber === value;

          return (
            // biome-ignore lint/a11y/useSemanticElements: star rating uses button+role="radio" for custom styling — native <input type="radio"> is not feasible here
            <button
              key={i}
              ref={(el) => {
                starRefs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={isChecked}
              aria-label={`${starNumber} star${starNumber !== 1 ? 's' : ''}`}
              tabIndex={disabled ? -1 : i === tabEntryIndex ? 0 : -1}
              disabled={disabled}
              data-filled={isFilled}
              className={cn(
                ratingStarVariants({ size }),
                'rounded-sm cursor-pointer',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
                'disabled:pointer-events-none',
                isFilled ? 'text-primary' : 'text-muted-foreground/40',
              )}
              onClick={() => commitValue(starNumber)}
              onMouseEnter={() => !disabled && setHoverValue(starNumber)}
              onMouseLeave={() => setHoverValue(null)}
              onKeyDown={(e) => handleKeyDown(e, i)}
            >
              <Star
                className={cn(
                  'w-full h-full',
                  isFilled ? 'fill-current stroke-primary' : 'fill-none stroke-current',
                )}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>
    );
  },
);

Rating.displayName = 'Rating';

export { Rating };
